import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import * as ValidationPatterns from '../../../shared/validation/custom-validation-patterns.const';
import CustomValidators from '../../../shared/validation/custom-validators';
import {ForgotPasswordService} from '../../../trouble-signing-in/services/forgot-password.service';
import {Router} from '@angular/router';
import {trigger} from '@angular/animations';
import {smoothEnterLeaveAnimation} from '../../../shared/animations/custom-animations';
import {IS_NEW_PASSWORD_VALIDATION_ERROR_CODE, NAME_IN_PASSWORD_ERROR_CODE, ValidationErrorResponse} from '../../../shared/models/validation-error-response';
import {PHONE_NUMBER_LIST} from "../../../shared/constants/phone-numbers.const";
import {DomainNameParserService} from 'src/main/resources/static/angular2/app/shared/services/domain-name-parser.service';
import {UsernameAsyncValidator} from 'src/main/resources/static/angular2/app/registration/validation/username-async-validator';

@Component({
  selector: 'app-reset-password-form',
  templateUrl: './reset-password-form.component.html',
  styleUrls: [
    './reset-password-form.component.css',
    './../../../../assets/css/floating-labels.css'
  ],
  animations: [
    trigger('showGenericErrorAlert', smoothEnterLeaveAnimation('100ms', '100ms')),
    trigger('showBlacklistedPasswordAlert', smoothEnterLeaveAnimation('100ms', '100ms'))
  ]
})
export class ResetPasswordFormComponent implements OnInit {

  resetPasswordFormSubmitted = false;
  genericErrorAlertShown = false;
  blacklistedPasswordErrorAlertShown = false;
  missingUsernameAndOtpAlertShown = false;
  customerServicePhone: string;

  private readonly username: string;
  private readonly otp: string;

  resetPasswordForm = new FormGroup({
    passwordInput: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(40),
        Validators.pattern(ValidationPatterns.PASSWORD_VALIDATION_PATTERN),
        CustomValidators.atLeastOneLowerCaseCharacterValidator,
        CustomValidators.atLeastOneUpperCaseCharacterValidator,
        CustomValidators.atLeastOneDigitValidator,
        CustomValidators.atLeastOneSpecialCharacterValidator,
        CustomValidators.repeatingCharsValidator,
        CustomValidators.consecutiveCharsValidator
      ],
      asyncValidators: [this.usernameAsyncValidator.validatePasswordErrors.bind(this.usernameAsyncValidator)]
    }),
    confirmPasswordInput: new FormControl('', [Validators.required])
  }, {
    validators: [CustomValidators.getConfirmPasswordValidator]
  });

  constructor(private forgotPasswordService: ForgotPasswordService,
              private router: Router,
              private domainNameParserService: DomainNameParserService,
              private usernameAsyncValidator: UsernameAsyncValidator) {
    this.customerServicePhone = PHONE_NUMBER_LIST[this.domainNameParserService.getState()];
    this.username = this.router.getCurrentNavigation().extras?.state?.username;
    this.otp = this.router.getCurrentNavigation().extras?.state?.otp;
    if (!this.username || !this.otp) {
      console.error('Error while navigating to reset password page. No username or otp value defined.');
      this.missingUsernameAndOtpAlertShown = true;
    }
  }

  ngOnInit(): void {
  }

  get formControls(): { [p: string]: AbstractControl } {
    return this.resetPasswordForm.controls;
  }

  public submitResetPasswordForm(): void {
    this.resetPasswordFormSubmitted = true;
    // stop here if form is invalid
    if (this.resetPasswordForm.invalid) {
      return;
    }

    this.forgotPasswordService.changePassword(this.username, this.otp, this.getPasswordValue()).subscribe(validationErrorResponse => {
      if (validationErrorResponse == null) {
        this.router.navigate(['/memberLogin']);
      } else if (this.containsIsNewPasswordValidationError(validationErrorResponse)) {
        // show old value password error
        this.resetPasswordForm.controls.passwordInput.setErrors({oldPasswordValue: true});
      } else if (this.containsNameInPasswordError(validationErrorResponse)) {
        // show blacklisted terms in password error
        this.blacklistedPasswordErrorAlertShown = true;
      } else {
        console.error('ServiceLayer registration endpoint returned validation error. ' +
          'Details: ' + JSON.stringify(validationErrorResponse));
        this.genericErrorAlertShown = true;
      }
    }, error => {
      console.error('ServiceLayer returned error. Details: ' + JSON.stringify(error));
      this.genericErrorAlertShown = true;
    });
  }

  private getPasswordValue(): string {
    return this.resetPasswordForm.controls.passwordInput.value;
  }

  containsIsNewPasswordValidationError(validationResponse: ValidationErrorResponse): boolean {
    for (const validationError of validationResponse?.errors) {
      if (validationError.code === IS_NEW_PASSWORD_VALIDATION_ERROR_CODE) {
        return true;
      }
    }
    return false;
  }

  private containsNameInPasswordError(validationResponse: ValidationErrorResponse): boolean {
    for (const validationError of validationResponse?.errors) {
      if (validationError.code === NAME_IN_PASSWORD_ERROR_CODE) {
        return true;
      }
    }
    return false;
  }
}
